import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import styles from "./HeaderLoginDropdown.module.css";

const FIREBASE_ERROR_MESSAGES = {
  "auth/user-not-found": "No account found with this email.",
  "auth/wrong-password": "Incorrect password.",
  "auth/invalid-email": "Invalid email address.",
  "auth/user-disabled": "Account disabled.",
  "auth/too-many-requests": "Too many attempts. Try later.",
  "auth/invalid-credential": "Invalid email or password.",
  "auth/network-request-failed": "Network error.",
};

function getAuthErrorMessage(code) {
  return FIREBASE_ERROR_MESSAGES[code] || "Login failed. Please try again.";
}

export default function HeaderLoginDropdown({ onClose }) {
  const { login, loginWithGoogle } = useAuth();
  const dropdownRef = useRef(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      onClose();
    } catch (err) {
      setError(getAuthErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleLogin() {
    setError("");
    setGoogleLoading(true);
    try {
      await loginWithGoogle();
      onClose();
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        setError(getAuthErrorMessage(err.code));
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
      <div className={styles.header}>
        <span className={styles.title}>Sign In</span>
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <button
        type="button"
        className={styles.googleBtn}
        onClick={handleGoogleLogin}
        disabled={loading || googleLoading}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
        {googleLoading ? "Signing in..." : "Continue with Google"}
      </button>

      <div className={styles.divider}><span>or</span></div>

      <form onSubmit={handleSubmit} noValidate>
        <input
          type="email"
          className={styles.input}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          autoComplete="email"
          required
          disabled={loading || googleLoading}
        />
        <div className={styles.passwordRow}>
          <input
            type={showPassword ? "text" : "password"}
            className={styles.input}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            autoComplete="current-password"
            required
            disabled={loading || googleLoading}
          />
          <button
            type="button"
            className={styles.eyeBtn}
            onClick={() => setShowPassword((v) => !v)}
            tabIndex={-1}
          >
            {showPassword ? "🙈" : "👁"}
          </button>
        </div>
        <button
          type="submit"
          className={styles.submitBtn}
          disabled={loading || googleLoading}
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>

      <div className={styles.footer}>
        <Link to="/forgot-password" className={styles.link} onClick={onClose}>Forgot password?</Link>
        <span className={styles.sep}>•</span>
        <Link to="/signup" className={styles.link} onClick={onClose}>Create account</Link>
      </div>
    </div>
  );
}
